/**
 * Module: `export/html-tables-to-gfm.ts` — HTML `<table>` → GFM pipe tables for PDF.
 * Word already renders HTML tables via md2docx; PDF only understands GFM.
 */

const HTML_TABLE_RE = /<table\b[^>]*>[\s\S]*?<\/table>/gi;

function escapeGfmCell(value: string): string {
  return value.replace(/\|/g, "\\|").replace(/\r?\n/g, " ").trim();
}

function serializeCell(el: Element): string {
  const clone = el.cloneNode(true) as Element;
  for (const br of clone.querySelectorAll("br")) {
    br.replaceWith(" ");
  }
  for (const anchor of clone.querySelectorAll("a")) {
    const href = (anchor.getAttribute("href") ?? "").trim();
    const label = (anchor.textContent ?? "").replace(/\s+/g, " ").trim();
    anchor.replaceWith(href ? `[${label}](${href})` : label);
  }
  return escapeGfmCell((clone.textContent ?? "").replace(/\s+/g, " "));
}

function htmlTableToGfm(tableHtml: string): string | null {
  const doc = new DOMParser().parseFromString(tableHtml, "text/html");
  const table = doc.querySelector("table");
  if (!table) return null;

  const rows = [...table.querySelectorAll("tr")].map((row) =>
    [...row.querySelectorAll("th, td")].map(serializeCell)
  );
  const colCount = Math.max(0, ...rows.map((row) => row.length));
  if (colCount === 0 || rows.length === 0) return null;

  const padded = rows.map((row) => {
    const next = [...row];
    while (next.length < colCount) next.push("");
    return next;
  });

  const header = padded[0]!;
  const separator = header.map(() => "---");
  const lines = [
    `| ${header.join(" | ")} |`,
    `| ${separator.join(" | ")} |`,
    ...padded.slice(1).map((row) => `| ${row.join(" | ")} |`),
  ];
  return `\n\n${lines.join("\n")}\n\n`;
}

/** Replaces HTML tables in markdown with GFM pipe tables. Leaves other HTML untouched. */
export function htmlTablesToGfm(markdown: string): string {
  if (!markdown.toLowerCase().includes("<table")) return markdown;
  return markdown.replace(HTML_TABLE_RE, (tableHtml) => {
    return htmlTableToGfm(tableHtml) ?? tableHtml;
  });
}
